import { db, type CachedDebt, type CachedProduct, type CachedProductUnit } from "./db";
import type { CartLine } from "./cart";
import {
  calculateSaleCogs,
  calculateWeightedAverageCost,
  deriveFinancialEffects,
  roundQuantity,
} from "@bledger/domain";

export interface LocalOperationOptions {
  tenantId?: string;
  storeId?: string;
  demo?: boolean;
}

export interface ApplyLocalSaleOptions extends LocalOperationOptions {
  allowNegativeStock?: boolean;
}

export interface LocalReceiveLine {
  product: CachedProduct;
  unit: CachedProductUnit | null;
  quantity: number;
  multiplier: number;
  unitCost: number;
  lotCode?: string;
  expiryDate?: string;
}

export interface ApplyLocalReceiveOptions extends LocalOperationOptions {
  note?: string;
}

export interface ApplyLocalAdjustmentOptions extends LocalOperationOptions {
  reason?: string;
  unitCost?: number;
  note?: string;
  lotCode?: string | null;
  inventoryLotId?: string | null;
  expiryDate?: string | null;
}

interface Dashboard {
  revenue: number;
  orderCount: number;
  estimatedProfit: number;
  accounts: Array<{
    id: string;
    name: string;
    type: "CASH" | "BANK";
    balance: number;
  }>;
  alerts: {
    lowStock: number;
    expiringLots?: number;
    pendingPayments: number;
  };
}

function getActiveContext(options?: LocalOperationOptions): { tenantId: string; storeId: string } {
  if (options?.tenantId && options?.storeId) {
    return { tenantId: options.tenantId, storeId: options.storeId };
  }
  try {
    const raw = localStorage.getItem("bledger.session");
    if (raw) {
      const s = JSON.parse(raw);
      return {
        tenantId: options?.tenantId || s?.tenant?.id || "default",
        storeId: options?.storeId || s?.store?.id || "default",
      };
    }
  } catch {}
  return {
    tenantId: options?.tenantId || "default",
    storeId: options?.storeId || "default",
  };
}

async function getDashboard(): Promise<Dashboard> {
  const record = await db.meta.get("dashboard");
  return (record?.value as Dashboard | undefined) ?? {
    revenue: 0,
    orderCount: 0,
    estimatedProfit: 0,
    accounts: [
      {
        id: "local-cash",
        name: "Két tiền mặt",
        type: "CASH",
        balance: 0,
      },
      {
        id: "local-bank",
        name: "Tài khoản ngân hàng",
        type: "BANK",
        balance: 0,
      },
    ],
    alerts: {
      lowStock: 0,
      expiringLots: 0,
      pendingPayments: 0,
    },
  };
}

async function saveDashboard(dashboard: Dashboard): Promise<void> {
  await db.meta.put({ key: "dashboard", value: dashboard });
}

async function getDebts(): Promise<CachedDebt[]> {
  const record = await db.meta.get("debts");
  return (record?.value as CachedDebt[] | undefined) ?? [];
}

async function increaseLocalDebt(
  counterpartyType: "CUSTOMER" | "SUPPLIER",
  counterpartyId: string,
  name: string,
  phone: string | null | undefined,
  amount: number,
): Promise<void> {
  const debts = await getDebts();
  const found = debts.find(
    (item) =>
      item.counterpartyType === counterpartyType &&
      item.counterpartyId === counterpartyId,
  );

  if (found) {
    found.balance += amount;
    found.lastActivityAt = new Date().toISOString();
  } else {
    debts.push({
      counterpartyType,
      counterpartyId,
      name,
      phone,
      balance: amount,
      lastActivityAt: new Date().toISOString(),
    });
  }

  await db.meta.put({ key: "debts", value: debts });
}

/**
 * P0 — OFFLINE OUTBOX ATOMICITY:
 * Atomically records LocalSale, local inventory projection, money/debt projection,
 * dashboard projection, and outbox command in the SAME Dexie transaction.
 */
export async function applyLocalSale(
  lines: CartLine[],
  paymentMethod: "CASH" | "BANK_TRANSFER" | "DEBT",
  idempotencyKey: string,
  customerId?: string | null,
  options?: ApplyLocalSaleOptions,
): Promise<number> {
  const total = lines.reduce(
    (sum, item) => sum + Math.round(item.unitPrice * item.quantity),
    0,
  );
  const financial = deriveFinancialEffects({
    type: "SALE",
    totalAmount: total,
    cashAmount: paymentMethod === "DEBT" ? 0 : total,
  });

  const profit = lines.reduce((sum, item) => {
    const baseQuantity = item.quantity * item.multiplier;
    const itemRevenue = Math.round(item.unitPrice * item.quantity);
    const itemCost = calculateSaleCogs(baseQuantity, item.product.avgCost);
    return sum + (itemRevenue - itemCost);
  }, 0);

  const context = getActiveContext(options);

  await db.transaction(
    "rw",
    [db.products, db.sales, db.money, db.customers, db.meta, db.syncQueue],
    async () => {
      const requestedByProduct = new Map<string, number>();

      for (const item of lines) {
        requestedByProduct.set(
          item.product.id,
          (requestedByProduct.get(item.product.id) ?? 0) +
            item.quantity * item.multiplier,
        );
      }

      for (const [productId, requested] of requestedByProduct) {
        const product = await db.products.get(productId);
        if (!product) {
          throw new Error("Không tìm thấy sản phẩm");
        }
        if (!options?.allowNegativeStock && product.stockQty + 0.0005 < requested) {
          throw new Error(`Tồn kho ${product.name} không đủ`);
        }
        await db.products.update(productId, {
          stockQty: roundQuantity(product.stockQty - requested),
        });
      }

      await db.sales.put({
        id: idempotencyKey,
        code: `LOCAL-${idempotencyKey.slice(0, 8).toUpperCase()}`,
        total,
        paymentMethod,
        createdAt: new Date().toISOString(),
        syncStatus: "PENDING",
      });

      if (financial.receivableDelta > 0) {
        if (!customerId) throw new Error("Ghi nợ cần khách hàng");
        const customer = await db.customers.get(customerId);
        if (!customer) throw new Error("Không tìm thấy khách hàng");

        await increaseLocalDebt(
          "CUSTOMER",
          customer.id,
          customer.name,
          customer.phone,
          financial.receivableDelta,
        );
      } else {
        await db.money.put({
          id: `money-${idempotencyKey}`,
          direction: "IN",
          amount: Math.abs(financial.cashDelta),
          category: "Bán hàng",
          accountType: paymentMethod === "CASH" ? "CASH" : "BANK",
          createdAt: new Date().toISOString(),
          syncStatus: "PENDING",
        });
      }

      const dashboard = await getDashboard();
      dashboard.revenue += total;
      dashboard.orderCount += 1;
      dashboard.estimatedProfit += profit;

      if (financial.cashDelta !== 0) {
        const type = paymentMethod === "CASH" ? "CASH" : "BANK";
        const account = dashboard.accounts.find((item) => item.type === type);
        if (account) account.balance += financial.cashDelta;
      }

      dashboard.alerts.lowStock = (
        await db.products.toArray()
      ).filter((product) => product.stockQty <= product.minStockQty).length;

      await saveDashboard(dashboard);

      // P0 — Atomic Outbox Command Insertion in SAME transaction
      if (!options?.demo) {
        await db.syncQueue.put({
          id: idempotencyKey,
          tenantId: context.tenantId,
          storeId: context.storeId,
          operation: "SALE",
          type: "SALE",
          endpoint: "/sales",
          payload: {
            idempotencyKey,
            lines: lines.map((line) => ({
              productId: line.product.id,
              unitId: line.unit?.id ?? null,
              quantity: line.quantity,
            })),
            paymentMethod,
            customerId: paymentMethod === "DEBT" ? customerId : null,
            allowNegativeStock: Boolean(options?.allowNegativeStock),
          },
          idempotencyKey,
          status: "PENDING",
          attemptCount: 0,
          createdAt: new Date().toISOString(),
        });
      }
    },
  );

  return total;
}

/**
 * P0 — OFFLINE OUTBOX ATOMICITY:
 * Atomically records Goods Receipt, updates inventory and moving weighted average cost,
 * updates money/debt, and writes Outbox command in the SAME Dexie transaction.
 */
export async function applyLocalReceiveStock(
  lines: LocalReceiveLine[],
  paymentMethod: "CASH" | "BANK_TRANSFER" | "DEBT",
  idempotencyKey: string,
  supplierId?: string | null,
  options?: ApplyLocalReceiveOptions,
): Promise<number> {
  const total = lines.reduce(
    (sum, item) => sum + Math.round(item.quantity * item.unitCost),
    0,
  );
  const financial = deriveFinancialEffects({
    type: "PURCHASE",
    totalAmount: total,
    cashAmount: paymentMethod === "DEBT" ? 0 : total,
  });

  const context = getActiveContext(options);

  await db.transaction(
    "rw",
    [db.products, db.money, db.suppliers, db.meta, db.syncQueue],
    async () => {
      const incomingByProduct = new Map<
        string,
        { quantity: number; costValue: number }
      >();

      for (const item of lines) {
        const baseQuantity = item.quantity * item.multiplier;
        const baseUnitCost =
          item.multiplier > 0
            ? Math.round(item.unitCost / item.multiplier)
            : item.unitCost;

        const current = incomingByProduct.get(item.product.id) ?? {
          quantity: 0,
          costValue: 0,
        };

        current.quantity += baseQuantity;
        current.costValue += baseQuantity * baseUnitCost;
        incomingByProduct.set(item.product.id, current);
      }

      for (const [productId, incoming] of incomingByProduct) {
        const product = await db.products.get(productId);
        if (!product) throw new Error("Không tìm thấy sản phẩm");

        const nextQty = roundQuantity(product.stockQty + incoming.quantity);
        const incomingAvg = incoming.quantity > 0
          ? Math.round(incoming.costValue / incoming.quantity)
          : 0;

        const nextAvg = calculateWeightedAverageCost(
          product.stockQty,
          product.avgCost,
          incoming.quantity,
          incomingAvg,
        );

        await db.products.update(productId, {
          stockQty: nextQty,
          avgCost: nextAvg,
        });
      }

      const dashboard = await getDashboard();

      if (financial.payableDelta > 0) {
        if (!supplierId) {
          throw new Error("Nhập hàng ghi nợ cần nhà cung cấp");
        }

        const supplier = await db.suppliers.get(supplierId);
        if (!supplier) {
          throw new Error("Không tìm thấy nhà cung cấp");
        }

        await increaseLocalDebt(
          "SUPPLIER",
          supplier.id,
          supplier.name,
          supplier.phone,
          financial.payableDelta,
        );
      } else {
        await db.money.put({
          id: `money-${idempotencyKey}`,
          direction: "OUT",
          amount: Math.abs(financial.cashDelta),
          category: "Nhập hàng",
          accountType: paymentMethod === "CASH" ? "CASH" : "BANK",
          createdAt: new Date().toISOString(),
          syncStatus: "PENDING",
        });

        const type = paymentMethod === "CASH" ? "CASH" : "BANK";
        const account = dashboard.accounts.find((item) => item.type === type);
        if (account) account.balance += financial.cashDelta;
      }

      dashboard.alerts.lowStock = (
        await db.products.toArray()
      ).filter((product) => product.stockQty <= product.minStockQty).length;

      await saveDashboard(dashboard);

      // P0 — Atomic Outbox Command Insertion in SAME transaction
      if (!options?.demo) {
        await db.syncQueue.put({
          id: idempotencyKey,
          tenantId: context.tenantId,
          storeId: context.storeId,
          operation: "RECEIVE_STOCK",
          type: "RECEIVE_STOCK",
          endpoint: "/inventory/receive",
          payload: {
            idempotencyKey,
            supplierId: supplierId || null,
            paymentMethod,
            note: options?.note,
            lines: lines.map((line) => ({
              productId: line.product.id,
              unitId: line.unit?.id ?? null,
              quantity: line.quantity,
              unitCost: Math.round(line.unitCost),
              lotCode: line.lotCode?.trim() || null,
              expiryDate: line.expiryDate || null,
            })),
          },
          idempotencyKey,
          status: "PENDING",
          attemptCount: 0,
          createdAt: new Date().toISOString(),
        });
      }
    },
  );

  return total;
}

/**
 * P0 — OFFLINE OUTBOX ATOMICITY:
 * Stock adjustment applied locally and queued atomically in Dexie.
 */
export async function applyLocalAdjustment(
  product: CachedProduct,
  delta: number,
  idempotencyKey?: string,
  options?: ApplyLocalAdjustmentOptions,
): Promise<void> {
  const key = idempotencyKey || crypto.randomUUID();
  const context = getActiveContext(options);

  await db.transaction("rw", [db.products, db.meta, db.syncQueue], async () => {
    const nextAvgCost =
      delta > 0
        ? calculateWeightedAverageCost(
            product.stockQty,
            product.avgCost,
            delta,
            options?.unitCost ?? 0,
          )
        : product.avgCost;

    await db.products.update(product.id, {
      stockQty: roundQuantity(product.stockQty + delta),
      avgCost: nextAvgCost,
    });

    const dashboard = await getDashboard();
    dashboard.alerts.lowStock = (
      await db.products.toArray()
    ).filter((candidate) => candidate.stockQty <= candidate.minStockQty).length;

    await saveDashboard(dashboard);

    if (!options?.demo) {
      await db.syncQueue.put({
        id: key,
        tenantId: context.tenantId,
        storeId: context.storeId,
        operation: "STOCK_ADJUSTMENT",
        type: "STOCK_ADJUSTMENT",
        endpoint: "/inventory/adjust",
        payload: {
          idempotencyKey: key,
          productId: product.id,
          delta,
          reason: options?.reason || "STOCKTAKE",
          note: options?.note,
          unitCost: delta > 0 ? options?.unitCost : undefined,
          lotCode: options?.lotCode || null,
          inventoryLotId: options?.inventoryLotId || null,
          expiryDate: options?.expiryDate || null,
        },
        idempotencyKey: key,
        status: "PENDING",
        attemptCount: 0,
        createdAt: new Date().toISOString(),
      });
    }
  });
}

/**
 * P0 — OFFLINE OUTBOX ATOMICITY:
 * Cash movement recorded locally and queued atomically in Dexie.
 */
export async function applyLocalCash(
  direction: "IN" | "OUT",
  amount: number,
  category: string,
  accountType: "CASH" | "BANK",
  idempotencyKey: string,
  note?: string,
  options?: LocalOperationOptions,
): Promise<void> {
  const context = getActiveContext(options);

  await db.transaction("rw", [db.money, db.meta, db.syncQueue], async () => {
    await db.money.put({
      id: `money-${idempotencyKey}`,
      direction,
      amount,
      category,
      accountType,
      createdAt: new Date().toISOString(),
      syncStatus: "PENDING",
    });

    const dashboard = await getDashboard();
    const account = dashboard.accounts.find((item) => item.type === accountType);

    if (account) {
      account.balance += direction === "IN" ? amount : -amount;
    }

    await saveDashboard(dashboard);

    if (!options?.demo) {
      await db.syncQueue.put({
        id: idempotencyKey,
        tenantId: context.tenantId,
        storeId: context.storeId,
        operation: "CASH_MOVEMENT",
        type: "CASH_MOVEMENT",
        endpoint: "/cash/movements",
        payload: {
          idempotencyKey,
          direction,
          amount,
          category,
          accountType,
          note,
        },
        idempotencyKey,
        status: "PENDING",
        attemptCount: 0,
        createdAt: new Date().toISOString(),
      });
    }
  });
}
